(() => {
  if (customElements.get('ff-r6-board-compare')) return;

  const storageKey = 'ff_compare';
  const instances = new Set();
  const rows = [
    ['Sport', 'sport'],
    ['Best for', 'bestFor'],
    ['Conditions', 'conditions'],
    ['Stability', 'stability'],
    ['Maneuverability', 'maneuverability'],
    ['Transport', 'transport'],
    ['Rider it suits', 'rider']
  ];
  let selection = null;

  const cleanIDs = (value, limit = 3) => Array.isArray(value)
    ? [...new Set(value.filter(id => typeof id === 'string' && id.trim() && id.length <= 200).map(id => id.trim()))].slice(0, limit)
    : null;
  const safeURL = value => {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, document.baseURI);
      return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url.href : null;
    } catch { return null; }
  };
  const cell = (role, className, text) => {
    const node = document.createElement('div');
    node.className = 'ff-compare__cell' + (className ? ' ' + className : '');
    node.setAttribute('role', role);
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const knownSelection = () => (selection || []).filter(id => [...instances].some(instance => instance.boards.has(id)));
  const publish = ids => {
    selection = cleanIDs(ids) || [];
    try { sessionStorage.setItem(storageKey, JSON.stringify(selection)); } catch {}
    for (const instance of instances) instance.render();
  };

  class FFBoardCompare extends HTMLElement {
    connectedCallback() {
      const connection = this.connection = (this.connection || 0) + 1;
      queueMicrotask(() => {
        if (this.isConnected && this.connection === connection) this.start();
      });
    }

    disconnectedCallback() {
      this.connection = (this.connection || 0) + 1;
      this.stop();
    }

    owned(selector) {
      return [...this.querySelectorAll(selector)].filter(node => node.closest('ff-r6-board-compare') === this);
    }

    start() {
      if (!this.isConnected || this.controller) return;
      const data = this.owned('script[data-ff-r6-compare-data]')[0];
      const table = this.owned('[data-ff-r6-compare-table]')[0];
      let input;
      try { input = data ? JSON.parse(data.textContent) : null; } catch {}
      if (!Array.isArray(input) || !table) {
        this.classList.add('is-pending');
        this.classList.remove('is-ready');
        if (!this.observer) {
          this.observer = new MutationObserver(() => this.start());
          this.observer.observe(this, { childList: true, subtree: true, characterData: true });
        }
        return;
      }
      const boards = new Map();
      for (const item of input) {
        if (!item || typeof item.id !== 'string' || !item.id.trim() || boards.has(item.id.trim())) continue;
        const board = {};
        for (const key of ['id', 'handle', 'name', 'sport', 'bestFor', 'conditions', 'stability', 'maneuverability', 'transport', 'rider', 'image', 'url', 'blurb', 'tag', 'brand']) {
          board[key] = typeof item[key] === 'string' ? item[key].trim() : '';
        }
        board.available = item.available === true;
        if (board.id.length <= 200 && board.name) boards.set(board.id, board);
      }
      if (!boards.size) {
        this.observer?.disconnect();
        this.observer = null;
        this.classList.add('is-pending');
        this.classList.remove('is-ready');
        return;
      }
      this.observer?.disconnect();
      this.observer = null;
      this.boards = boards;
      this.table = table;
      this.filter = this.filter || 'all';
      this.brand = this.brand || 'all';
      if (!this.owned('[data-ff-filter]').some(button => button.dataset.ffFilter === this.filter)) this.filter = 'all';
      if (selection === null) {
        const defaults = cleanIDs((this.dataset.defaultIds || '').split(',').map(id => id.trim())) || [];
        try {
          const saved = sessionStorage.getItem(storageKey);
          selection = saved === null ? defaults : cleanIDs(JSON.parse(saved)) || defaults;
        } catch { selection = defaults; }
      }
      this.controller = new AbortController();
      instances.add(this);
      if (this.pendingSelection) {
        publish(this.pendingSelection.filter(id => this.boards.has(id)));
        this.pendingSelection = null;
      }
      this.addEventListener('click', event => this.handleClick(event), { signal: this.controller.signal });
      this.addEventListener('change', event => {
        const control = event.target;
        if (!(control instanceof Element) || control.closest('ff-r6-board-compare') !== this) return;
        if (control.matches('input[type=checkbox][data-ff-stock]')) this.setInStockOnly(control.checked);
        if (control.matches('select[data-ff-brand]')) this.setBrand(control.value);
      }, { signal: this.controller.signal });
      this.table.setAttribute('role', 'table');
      if (!this.table.hasAttribute('aria-label')) this.table.setAttribute('aria-label', 'Compare boards');
      for (const status of this.owned('[data-ff-r6-compare-status]')) {
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('aria-atomic', 'true');
      }
      for (const button of this.owned('button[data-ff-filter], button[data-ff-r6-compare-toggle], button[data-ff-r6-compare-clear], button[data-ff-r6-compare-more], button[data-ff-brand], button[data-ff-stock]')) button.type = 'button';
      this.render();
      this.classList.remove('is-pending');
      this.classList.add('is-ready');
      this.dispatchEvent(new CustomEvent('ff:compare:ready', { bubbles: true }));
      for (const instance of instances) if (instance !== this) instance.render();
    }

    stop() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.observer = null;
      instances.delete(this);
      this.classList.remove('is-ready');
      this.classList.add('is-pending');
      for (const card of this.owned('[data-ff-board]')) card.hidden = false;
      for (const instance of instances) instance.render();
    }

    selected() {
      return (selection || []).filter(id => this.boards.has(id));
    }

    announce(message) {
      for (const status of this.owned('[data-ff-r6-compare-status]')) status.textContent = message;
    }

    refreshFilter() {
      if (!this.controller) return;
      this.renderCards();
      this.announce(this.visibleCount + ' boards shown. ' + this.selected().length + ' in comparison.');
    }

    setFilter(sport) {
      if (typeof sport !== 'string' || !sport.trim()) return false;
      this.filter = sport.trim();
      this.refreshFilter();
      return true;
    }

    setSelection(ids) {
      const next = cleanIDs(ids);
      if (!next) return false;
      if (!this.controller) this.pendingSelection = next;
      else {
        publish(next.filter(id => this.boards.has(id)));
        this.announce(this.selected().length + ' boards in comparison.');
      }
      return true;
    }

    setBoardFilter(ids) {
      if (ids !== null && !Array.isArray(ids)) return false;
      this.boardFilter = ids === null ? null : new Set(cleanIDs(ids, Infinity));
      this.refreshFilter();
      return true;
    }

    setBrand(brand) {
      if (typeof brand !== 'string' || !brand.trim()) return false;
      this.brand = brand.trim();
      this.refreshFilter();
      return true;
    }

    setInStockOnly(enabled) {
      if (typeof enabled !== 'boolean') return false;
      this.inStockOnly = enabled;
      this.refreshFilter();
      return true;
    }

    handleClick(event) {
      if (!(event.target instanceof Element) || event.target.closest('ff-r6-board-compare') !== this) return;
      const button = event.target.closest('[data-ff-r6-compare-toggle], [data-ff-r6-compare-remove], [data-ff-filter], [data-ff-r6-compare-clear], [data-ff-r6-compare-more], button[data-ff-brand], button[data-ff-stock]');
      if (!button || button.closest('ff-r6-board-compare') !== this) return;
      event.preventDefault();
      if (button.hasAttribute('data-ff-brand')) {
        this.setBrand(button.dataset.ffBrand);
        return;
      }
      if (button.hasAttribute('data-ff-stock')) {
        this.setInStockOnly(!this.inStockOnly);
        return;
      }
      if (button.hasAttribute('data-ff-filter')) {
        this.setFilter(button.dataset.ffFilter);
        return;
      }
      if (button.hasAttribute('data-ff-r6-compare-more')) {
        this.expanded = !this.expanded;
        this.renderCards();
        this.announce(this.visibleCount + ' boards shown.');
        return;
      }
      if (button.hasAttribute('data-ff-r6-compare-clear')) {
        publish([]);
        this.announce('Comparison cleared. Pick boards from the lineup above.');
        return;
      }
      const id = button.dataset.ffR6CompareRemove || button.dataset.ffR6CompareToggle;
      const board = this.boards.get(id);
      if (!board) return;
      const current = knownSelection();
      if (current.includes(id)) {
        const wasRemove = button.hasAttribute('data-ff-r6-compare-remove');
        publish(current.filter(value => value !== id));
        this.announce(board.name + ' removed. ' + this.selected().length + ' boards in comparison.');
        if (wasRemove) {
          const cardButton = this.owned('[data-ff-r6-compare-toggle]').find(node => node.dataset.ffR6CompareToggle === id && !node.closest('[data-ff-board]')?.hidden);
          const focusTarget = cardButton || this.owned('[data-ff-r6-compare-remove]')[0] || this.owned('[data-ff-r6-compare-clear]')[0];
          focusTarget?.focus({ preventScroll: true });
        }
      } else if (!button.hasAttribute('data-ff-r6-compare-remove')) {
        if (current.length >= 3) {
          this.announce('You can compare up to 3 boards. Remove a board to add another.');
          return;
        }
        publish([...current, id]);
        this.announce(board.name + ' added. ' + this.selected().length + ' boards in comparison.');
      }
    }

    renderCards() {
      const selected = this.selected();
      const full = knownSelection().length >= 3;
      const more = this.owned('[data-ff-r6-compare-more]')[0];
      const limit = Number(this.dataset.initialVisible);
      const limited = more && Number.isInteger(limit) && limit > 0;
      const unfiltered = this.filter === 'all' && this.brand === 'all' && !this.inStockOnly && !this.boardFilter;
      let matches = 0;
      this.visibleCount = 0;
      for (const card of this.owned('[data-ff-board]')) {
        const board = this.boards.get(card.dataset.ffBoard);
        const match = (this.filter === 'all' || board?.sport === this.filter)
          && (this.brand === 'all' || board?.brand === this.brand)
          && (!this.inStockOnly || board?.available === true)
          && (!this.boardFilter || this.boardFilter.has(card.dataset.ffBoard));
        if (match) matches++;
        card.hidden = !match || Boolean(limited && unfiltered && !this.expanded && matches > limit);
        if (!card.hidden) this.visibleCount++;
      }
      for (const button of this.owned('[data-ff-r6-compare-toggle]')) {
        const id = button.dataset.ffR6CompareToggle;
        const active = selected.includes(id);
        button.textContent = active ? 'In compare ✓' : 'Add to compare';
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-disabled', String(!this.boards.has(id) || (!active && full)));
        button.disabled = false;
      }
      for (const button of this.owned('[data-ff-filter]')) button.setAttribute('aria-pressed', String(button.dataset.ffFilter === this.filter));
      for (const control of this.owned('[data-ff-brand]')) {
        if (control instanceof HTMLSelectElement) control.value = this.brand;
        else control.setAttribute('aria-pressed', String(control.dataset.ffBrand === this.brand));
      }
      for (const control of this.owned('[data-ff-stock]')) {
        if (control instanceof HTMLInputElement && control.type === 'checkbox') control.checked = Boolean(this.inStockOnly);
        else control.setAttribute('aria-pressed', String(Boolean(this.inStockOnly)));
      }
      if (more) {
        more.hidden = !limited || !unfiltered || matches <= limit;
        more.setAttribute('aria-expanded', String(Boolean(this.expanded)));
        more.textContent = this.expanded ? 'Show fewer boards' : 'Show all boards';
      }
    }

    renderTable() {
      const ids = this.selected();
      const columns = Math.max(ids.length, 1);
      this.style.setProperty('--ff-r6-compare-columns', String(columns));
      this.table.setAttribute('aria-colcount', String(columns + 1));
      this.table.setAttribute('aria-rowcount', String(rows.length + 1));
      const fragment = document.createDocumentFragment();
      const header = document.createElement('div');
      header.className = 'ff-compare__row';
      header.setAttribute('role', 'row');
      header.append(cell('columnheader', 'ff-compare__label', 'Board'));
      for (const id of ids) {
        const board = this.boards.get(id);
        const head = cell('columnheader', 'ff-compare__product');
        const url = safeURL(board.url);
        const link = document.createElement(url ? 'a' : 'span');
        link.className = 'ff-compare__product-link';
        if (url) link.href = url;
        const imageURL = safeURL(board.image);
        if (imageURL) {
          const image = document.createElement('img');
          image.src = imageURL;
          image.alt = '';
          image.width = 48;
          image.height = 48;
          image.loading = 'lazy';
          image.decoding = 'async';
          image.style.objectFit = 'contain';
          link.append(image);
        }
        const name = document.createElement('span');
        name.textContent = board.name;
        link.append(name);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.ffR6CompareRemove = id;
        remove.className = 'ff-compare__remove';
        remove.textContent = 'Remove';
        remove.setAttribute('aria-label', 'Remove ' + board.name + ' from comparison');
        head.append(link, remove);
        header.append(head);
      }
      if (!ids.length) header.append(cell('columnheader', 'ff-compare__product', 'Pick boards from the lineup above'));
      fragment.append(header);
      for (const [label, key] of rows) {
        const row = document.createElement('div');
        row.className = 'ff-compare__row';
        row.setAttribute('role', 'row');
        row.append(cell('rowheader', 'ff-compare__label', label));
        for (const id of ids) row.append(cell('cell', '', this.boards.get(id)[key] || '—'));
        if (!ids.length) row.append(cell('cell', '', '—'));
        fragment.append(row);
      }
      this.table.replaceChildren(fragment);
    }

    render() {
      if (!this.controller) return;
      this.renderCards();
      this.renderTable();
      for (const count of this.owned('[data-ff-r6-compare-count]')) count.textContent = String(this.selected().length);
    }
  }

  customElements.define('ff-r6-board-compare', FFBoardCompare);
  const elementsIn = target => target instanceof Element
    ? [target, ...target.querySelectorAll('ff-r6-board-compare')].filter(node => node.matches('ff-r6-board-compare'))
    : [];
  document.addEventListener('shopify:section:load', event => {
    for (const instance of elementsIn(event.target)) instance.start();
  });
  document.addEventListener('shopify:section:unload', event => {
    for (const instance of elementsIn(event.target)) instance.stop();
  });
})();
